import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import UserCrewMemberDTO from '@src/adapters/dto/user/UserCrewMemberDTO';
import { useGetCrewMemberLazy } from '@src/adapters/hooks/user/useGetCrewMember';
import { useSaveUserVerifyLazy } from '@src/adapters/hooks/user/useSaveUserVerify';
import useUserSendLinkLazy from '@src/adapters/hooks/user/useUserSendLink';
import { useSnackbar } from 'notistack';
import useRegenerateAndResendAvailabilityLinkLazy from '@src/adapters/hooks/user/useRegenerateAndResendAvailabilityLink';
import { Controller, useForm } from 'react-hook-form';
import { useSaveCrewMemeberLazy } from '@src/adapters/hooks/user/useSaveUserCrewMember';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface Props {
  id: string;
  open: boolean;
  close(verfied: boolean): void;
}

function Member({ close, id, open }: Props) {
  const { execute: get } = useGetCrewMemberLazy();
  const { execute: verify } = useSaveUserVerifyLazy();
  const { execute: send } = useUserSendLinkLazy();
  const { execute: save } = useSaveCrewMemeberLazy();
  const { execute: regenerate } = useRegenerateAndResendAvailabilityLinkLazy();
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    formState: { errors, isDirty },
    reset,
    watch,
    getValues,
  } = useForm<UserCrewMemberDTO>();

  const member = watch();

  useEffect(() => {
    get({ id }).then((result) => {
      reset(result.member);
    });
  }, [get, id, reset]);

  const handleClose = () => close(false);

  const handleVerify = async () => {
    const result = await verify({ id });
    if (result.verified) close(true);
  };

  const handleMessage = async () => {
    const result = await send({ id });
    if (result.sent) {
      enqueueSnackbar('Message Sent', { variant: 'success' });
    }
  };

  const handleRegenerate = async () => {
    const result = await regenerate({ id });
    if (result.sent) {
      enqueueSnackbar('Link Regenerated and Sent', { variant: 'success' });
    }
  };

  const onSave = async () => {
    const formData = getValues();

    save({ user: formData }).then((result) => {
      reset(result.user);
      close(true);
    });
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle sx={{ px: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}>
          <Typography variant="h6">Crew Member</Typography>

          <Box display="flex" gap={1}>
            {!member?.isVerified && (
              <Button onClick={handleVerify} variant="contained">
                Verify
              </Button>
            )}

            {member?.isVerified && (
              <>
                <Button onClick={handleMessage} variant="contained">
                  Message Link
                </Button>

                <Button onClick={handleRegenerate} variant="contained">
                  Regenerate Link and Send
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <form>
          <Grid container spacing={2} sx={{ pt: 3 }}>
            <Grid size={6}>
              <Controller
                name="firstName"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    error={!!errors.firstName}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="lastName"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Last Name" fullWidth />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Controller
                    name="phoneNumber"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Phone Number" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="disableNotificationUntil"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en-gb">
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(newValue: Dayjs | null) =>
                            field.onChange(newValue ? newValue.toDate() : null)
                          }
                          slots={{ textField: TextField }}
                          slotProps={{
                            field: {
                              clearable: true,
                            },
                            textField: {
                              label: 'Disable Notifications Until',
                              variant: 'outlined',
                              fullWidth: true,
                              sx: {
                                '& .MuiSvgIcon-root': {
                                  color: '#fff', //
                                },
                                '& .MuiInputBase-input': {
                                  color: '#fff',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#fff',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#fff',
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="departments"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Departments"
                        fullWidth
                        value={field.value?.map((d) => d.name).join(', ') ?? ''}
                        slotProps={{
                          input: {
                            readOnly: true,
                            sx: {
                              cursor: 'default',
                              color: 'text.secondary',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              pointerEvents: 'none',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'action.disabledBackground',
                            cursor: 'default',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="departmentRoles"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Roles"
                        fullWidth
                        value={
                          field.value
                            ? field.value.map((r) => r.name).join(', ')
                            : 'No Roles Selected'
                        }
                        slotProps={{
                          input: {
                            readOnly: true,
                            sx: {
                              cursor: 'default',
                              color: 'text.secondary',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              pointerEvents: 'none',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'action.disabledBackground',
                            cursor: 'default',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="yearsOfExperience"
                    control={control}
                    defaultValue={0}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Years of Experience"
                        fullWidth
                        slotProps={{
                          input: {
                            readOnly: true,
                            sx: {
                              cursor: 'default',
                              color: 'text.secondary',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              pointerEvents: 'none',
                            },
                          },
                          inputLabel: { shrink: true },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'action.disabledBackground',
                            cursor: 'default',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="referrers"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <TextField
                        label="Referrers"
                        fullWidth
                        value={field.value?.join(', ') ?? ''}
                        slotProps={{
                          input: {
                            readOnly: true,
                            sx: {
                              cursor: 'default',
                              color: 'text.secondary',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              pointerEvents: 'none',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'action.disabledBackground',
                            cursor: 'default',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={6}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Controller
                    name="address.firstLine"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Address Line 1" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="address.secondLine"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Address Line 2" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="address.town"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Town / City" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="address.postcode"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Postcode" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="address.country"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Country" fullWidth />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="productions"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <TextField
                        label="Previous Productions"
                        fullWidth
                        value={field.value?.join(', ') ?? ''}
                        slotProps={{
                          input: {
                            readOnly: true,
                            sx: {
                              cursor: 'default',
                              color: 'text.secondary',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              pointerEvents: 'none',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'action.disabledBackground',
                            cursor: 'default',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={6}>
              <Controller
                name="endorsers"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Endorsers"
                    slotProps={{
                      input: {
                        readOnly: true,
                        sx: {
                          cursor: 'default',
                          color: 'text.secondary',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          pointerEvents: 'none',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'action.disabledBackground',
                        cursor: 'default',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'transparent',
                      },
                      '& .MuiInputBase-input': {
                        color: 'text.secondary',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1, minWidth: 140 }}>
          {isDirty ? 'Cancel' : 'Close'}
        </Button>

        {member?.isVerified && (
          <Button
            variant="contained"
            disabled={!isDirty}
            onClick={onSave}
            sx={{ flex: 1, minWidth: 140 }}>
            Save Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default Member;
